import { BookOpen, MessageCircleQuestion } from "lucide-react";
import type { HelpGuideSummary } from "@ttc-spectree/toolbox-contract";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";

/**
 * Suggested questions and guide links in the Help panel (epic
 * PI-SPEC-SW-E302, feature PI-SPEC-SW-1557, frames H1 and H5, design `.sug`).
 * Both come from the bundled help pack, not from Claude, so they appear at
 * once: questions per screen (`packInfo.suggestions[screenKey]`), and the
 * guides, which open without Claude.
 */

const rowClassName =
  "flex w-full items-center gap-2.5 rounded-lg border border-[#1F1F23] bg-[#111113] px-3 py-[9px] text-left text-[13px] text-[#E4E4E7] transition-colors hover:border-[#27272A] hover:bg-[#151518] disabled:cursor-default disabled:opacity-60";

export function HelpSuggestions({
  questions,
  disabled,
  onAsk,
}: {
  questions: readonly string[];
  disabled?: boolean;
  onAsk: (question: string) => void;
}) {
  if (questions.length === 0) return null;
  return (
    <div className="flex flex-col gap-3.5" data-testid="help-suggestions">
      <span className={cn(TOOLBOX_TEXT.eyebrow, "mt-1")}>About this screen</span>
      {questions.map((question) => (
        <button
          key={question}
          type="button"
          className={rowClassName}
          disabled={disabled}
          onClick={() => {
            onAsk(question);
          }}
        >
          <MessageCircleQuestion
            className={cn("h-3.5 w-3.5 flex-none", TOOLBOX_TEXT.faint)}
            aria-hidden="true"
          />
          {question}
        </button>
      ))}
    </div>
  );
}

export function HelpGuideList({
  guides,
  heading,
  onOpen,
}: {
  guides: readonly HelpGuideSummary[];
  heading: string;
  onOpen: (guide: HelpGuideSummary) => void;
}) {
  if (guides.length === 0) return null;
  return (
    <div className="flex flex-col gap-3.5" data-testid="help-guides">
      <span className={TOOLBOX_TEXT.eyebrow}>{heading}</span>
      {guides.map((guide) => (
        <button
          key={guide.id}
          type="button"
          className={rowClassName}
          onClick={() => {
            onOpen(guide);
          }}
        >
          <BookOpen
            className={cn("h-3.5 w-3.5 flex-none", TOOLBOX_TEXT.faint)}
            aria-hidden="true"
          />
          {guide.title}
        </button>
      ))}
    </div>
  );
}
